import React from "react";
// The same calendar the client screen's empty appointment tab wears, and the
// one the first-run panel on this screen wears. One mark for all three,
// because they are the same thing missing — nothing booked. It carries its own
// tinted blob at 56px, so there is no wrapper to tint here.
import { ReactComponent as NoAppointmentsMark } from "../../../assets/svg/No data images/appointment.svg";
import { Button, PlusIcon } from "../../../components/ui";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import { dayShort, weekdayShort } from "../myTask";

export interface FreeDayPanelProps {
  /** The day being looked at. Today or later — a day that has gone is not this. */
  day: Date;
  /** Off, with its reason, while the workspace has no plan behind it. */
  gate: GateProps;
  /** Opens the booking dialog, already standing on this day. */
  onCreate: () => void;
  /** Layout only — the desktop card uses it to centre this down its height. */
  className?: string;
}

/**
 * What a day with nothing on it says, from today onwards: the mark, the day by
 * name, one line, and the way to fill it.
 *
 * Four parts where there used to be two. The compact state this replaces was a
 * headline and a sentence centred in a card the height of the screen, with the
 * only way to act on it a button at the top of the page — which read as a list
 * that had failed to draw rather than as a day with room in it. What the mark
 * and the button add is the two things that sentence could not say on its own:
 * that nothing is wrong, and what to do next.
 *
 * Set left rather than centred down the middle of the card, which is the one
 * thing that makes a block of four parts read as a block. Centred, the mark
 * floats over a ragged column — three different widths stacked with nothing to
 * line them up — and the button at the foot of it lands wherever the sentence
 * above happened to end. One left edge is what holds them together.
 *
 * The day is named rather than called "today", and without its year. Named
 * because this screen is walked day by day and the panel is the only thing on
 * it that says in words which day came back empty — the strip says it in a
 * pill among six others. Without the year because the strip's selected pill is
 * already carrying it one line up, and a sentence is not where somebody reads
 * a year off.
 *
 * Outlined rather than filled, and it is the only action in the card: there is
 * a filled Create on the strip above on a desktop and a floating one on a
 * phone, and two filled teal buttons on one screen both saying the same word
 * read as two different actions.
 *
 * One component for both shapes of the screen rather than two. The day's rows
 * are two different layouts at the two widths — a thread with a column of
 * marks beside it, against cards read top to bottom — because an appointment
 * carries six facts that have to be arranged differently. This carries four,
 * stacked, so the same block works at both and only its air changes.
 */
const FreeDayPanel: React.FC<FreeDayPanelProps> = ({
  day,
  gate,
  onCreate,
  className = "",
}) => (
  // Inset from the card's left edge rather than sitting against it: the block
  // is an invitation standing in the middle of an empty card, and one pressed
  // into the corner reads as a header the card forgot to fill in under.
  <div
    className={`flex flex-col items-start px-5 py-8 sm:px-10 md:py-10 lg:px-[72px] ${className}`}
  >
    {/* Decorative: the headline under it already says what this is. */}
    <NoAppointmentsMark
      aria-hidden
      className="h-12 w-12 flex-shrink-0 md:h-14 md:w-14"
    />

    {/* Sized and weighted as a heading but not marked up as one: this sits
        inside a card on a screen that already has a real heading above it, and
        a second in the document outline would claim a section that does not
        exist. The same call `EmptyState` makes. */}
    <p className="mt-4 text-[18px] font-semibold leading-snug text-[#00424B] md:text-[22px] 3xl:text-[24px]">
      Nothing planned for {weekdayShort(day)}, {dayShort(day)}
    </p>

    <p className="mt-1 text-[14px] leading-[1.6] text-ink-muted md:text-[16px] 3xl:text-[18px]">
      Create a task to plan your day.
    </p>

    {/* The air above it belongs to the block rather than to the button: a
        `Button` is styled by its variant and size and takes no layout from its
        caller — see the note at the top of that file. */}
    <div className="mt-6 md:mt-8">
      <Button
        variant="outline"
        size="md"
        startIcon={<PlusIcon className="h-4 w-4" />}
        onClick={onCreate}
        {...gate}
      >
        Create task
      </Button>
    </div>
  </div>
);

export default FreeDayPanel;
